import React from 'react';
import styles from './styles';
// general/context
import axios from 'axios';
import axiosConfig from '../../config/axios.config';
import {InstanceContext} from '../../InstanceContext';
import {UserContext} from '../../Context/UserContext';
import {ResponsiveContext} from '../../Context/ResponsiveContext';
import {TrialCache} from '../TrialCache';
import {Prompt} from 'react-router-dom';
// components
import TrialOverview from '../TrialOverview';
import PatientOverview from '../PatientOverview';
import {useHistory, useRouteMatch } from 'react-router-dom';

import { checkResponseErrorCodeAndRedirect } from '../../utils/errorHelper';

const {apiUrl} = axiosConfig;

// -- fetch filters
const fetchFilters = async (id, instance, email) => {
  try {
    const data = await axios.get(`${apiUrl}/rt/getFilterData?send=DiseaseStage,TreatmentStatus,Mutations,Labs,VitalSigns,LungFunctionTest,PrimaryPurpose,CardiovascularTests`); // removed 'axiosConfig.auth' as token is updated on login
    return data.data ? data.data : data;
  } catch(err) {
    console.error(err, 'Error occurred fetching filter data.')
    checkResponseErrorCodeAndRedirect(err)
  }
}


let patientTriageNcts = [];

const OverviewContainer = props => {

  // -- primary state: idle(form), save(modal/review), cancel(modal/review), complete(cleanup/reset).
  const [ovState, setOvState] = React.useState('idle')
  // -- deps/context
  const history = useHistory();
  const routeMatch = useRouteMatch();
  const location = history.location;
  const {type} = props;
  const {user} = React.useContext(UserContext);
  const userIsPI = user.accountType && user.accountType === 'PI';
  const {contextInstance} = React.useContext(InstanceContext);
  const {cache, saveToCache, removeFromCache, refreshCache} = React.useContext(TrialCache);
  const {addResizeEvent, removeResizeEvent} = React.useContext(ResponsiveContext);
  // -- vars for child
  const {id} = routeMatch.params;
  const instance = contextInstance || user.instance;
  let overviewData = cache[id] ? cache[id].data : null;
  const setOverviewData = cacheData => saveToCache(id, cacheData);
  // -- data store
  // console.log(cache, "DATA")
  const filters = cache.trialOverviewFilters ? cache.trialOverviewFilters.data : {};
  const setFilters = cacheData => saveToCache('trialOverviewFilters', cacheData);

  const allTrials = cache.allTrials.data;
  const allNcts = cache[userIsPI ? 'getTrials' : 'allTrials'].data
    ? cache[userIsPI ? 'getTrials' : 'allTrials'].data.map(trial => trial._id)
    : [];
  // -- trackers store
  const fetchedList = React.useRef({filters: false})
  const setHasFetched = (property, value) => fetchedList.current[property] = value;
  const hasFetched = fetchedList.current;
  // -- diff store
  const [diffState, setDiffState] = React.useState({});
  const diffRef = React.useRef({
    diff: {},
    count: 0,
  });
  const setDiff = (property, newValue) => {
    if (typeof property === 'object') {
      setDiffState({});
      diffRef.current.diff = {};
      diffRef.current.count = 0;
    } else {
      let count = diffRef.current.count + 1;
      let update = {
        ...diffRef.current.diff,
        [property]: newValue
      };
      if (newValue === null) {
        delete update[property];
        count--;
      }
      setDiffState(update)
      diffRef.current.diff = update;
      diffRef.current.count = count;
    }
  }
  React.useEffect(() => {
    if (Object.keys(diffState).length > 0) {
      setDiffState({});
      diffRef.current = {diff: {}, count: 0}
    }
  }, [id])

  // -- fetch filters
  if (!hasFetched.filters) {
    setHasFetched('filters', true);
    fetchFilters().then(filters => setFilters(filters))}

  // -- window display size state
  const getWindowDisplayDims = () => ({
    height: window.innerHeight - 48 + 'px',
    width: document.body.clientWidth - props.navSpacing + 'px'
  })
  const [windowDisplayDims, setWindowDisplayDims] = React.useState(getWindowDisplayDims())
  // -- add/cleanup resize event to responsive context on mount/unmount
  React.useLayoutEffect(() => {
    addResizeEvent('window_display_resize', () => setWindowDisplayDims(getWindowDisplayDims()));
    return(() => removeResizeEvent('window_display_resize'))
  }, [])
  // -- re-calc dims on navspacing change
  React.useLayoutEffect(() => {
    setWindowDisplayDims(getWindowDisplayDims());
  }, [props.navSpacing])

  const ChildComponent = type === 'registry' ? PatientOverview : TrialOverview;

  const [initialWindows, setInitialWindows] = React.useState(type === 'registry'
    ? ['patientInformation', 'studyDetails']
    : ['trialConfiguration', 'originalContent']
  );

  return (
    <React.Fragment>
      <Prompt
        message='You have unsaved changes to this trial. Are you sure you want to leave this page?'
        when={Object.keys(diffState).length > 0}
      />
      <ChildComponent
        initialWindows={initialWindows}
        setInitialWindows={setInitialWindows}
        navSpacing={props.navSpacing}
        deps={{
          id,
          instance,
          overviewData,
          setOverviewData,
          cache,
          diffRef,
          setDiff,
          diffState,
          user,
          windowDisplayDims,
          removeFromCache,
          filters,
          allTrials,
          allNcts,
          patientTriageNcts,
          refreshCache
        }}
        {...props}
      />
    </React.Fragment>
  );
}

export default OverviewContainer;
